/**
 * @file ShopUsersPage.tsx
 * @description Сотрудники магазина (phase08.5 shadcn).
 */

import type { ColumnDef } from "@tanstack/react-table";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { useCurrentContext } from "../../auth/hooks";
import { UserCreateModal } from "../../components/UserCreateModal";
import { UserMobileCard } from "../../components/UserMobileCard";
import { Alert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { ContextBadge } from "../../components/ui/ContextBadge";
import { DataTable } from "../../components/ui/data-table";
import { EditorialCard } from "../../components/ui/EditorialCard";
import { useAppShellOptional } from "../../components/ui/AppShell";
import { listAdminUsers, type AdminUser } from "../../lib/api-admin-users";
import { ApiError } from "../../lib/api-client";
import { ShopUserRowActions } from "./ShopUserRowActions";

export function ShopUsersPage() {
  const shell = useAppShellOptional();
  const isMobile = shell?.isMobile ?? false;
  const ctx = useCurrentContext();
  const shop = ctx?.shop ?? null;
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listAdminUsers());
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось загрузить пользователей",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function replaceUser(updated: AdminUser) {
    setUsers((prev) => {
      if (updated.contexts.length === 0) {
        return prev.filter((u) => u.id !== updated.id);
      }
      const exists = prev.some((u) => u.id === updated.id);
      return exists
        ? prev.map((u) => (u.id === updated.id ? updated : u))
        : [...prev, updated];
    });
  }

  if (!shop) {
    return <Alert severity="warning">Магазинный контекст не активен</Alert>;
  }

  const columns: ColumnDef<AdminUser>[] = [
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
    },
    {
      accessorKey: "displayName",
      header: "Имя",
      cell: ({ row }) => row.original.displayName ?? "—",
    },
    {
      id: "contexts",
      header: "Роль в магазине",
      enableSorting: false,
      cell: ({ row }) => {
        const shopCtx = row.original.contexts.filter(
          (c) => c.contextType === "SHOP" && c.shop?.id === shop.id,
        );
        if (shopCtx.length === 0) return <ContextBadge.Empty label="Без роли" />;
        return (
          <div className="flex flex-wrap gap-1">
            {shopCtx.map((c) => (
              <ContextBadge key={c.id} ctx={c} />
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Создан",
      cell: ({ row }) =>
        row.original.createdAt
          ? new Date(row.original.createdAt).toLocaleDateString("ru-RU")
          : "—",
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Действия</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <ShopUserRowActions user={row.original} shop={shop} onChanged={replaceUser} />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-editorial">Магазин · {shop.displayName}</div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">
            Сотрудники магазина
          </h1>
        </div>
        <Button onClick={() => setCreateOpen(true)}>Пригласить сотрудника</Button>
      </div>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : isMobile ? (
        loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="size-6 animate-spin text-primary" />
          </div>
        ) : users.length === 0 ? (
          <Alert severity="info">В магазине пока нет сотрудников</Alert>
        ) : (
          <div className="flex flex-col gap-3">
            {users.map((u) => {
              const shopCtx = u.contexts.filter(
                (c) => c.contextType === "SHOP" && c.shop?.id === shop.id,
              );
              return (
                <UserMobileCard
                  key={u.id}
                  user={u}
                  contextsLabel="Роль в магазине"
                  contextBadges={
                    shopCtx.length === 0 ? (
                      <ContextBadge.Empty label="Без роли" />
                    ) : (
                      shopCtx.map((c) => <ContextBadge key={c.id} ctx={c} />)
                    )
                  }
                  actions={
                    <ShopUserRowActions user={u} shop={shop} onChanged={replaceUser} />
                  }
                />
              );
            })}
          </div>
        )
      ) : (
        <EditorialCard padded={false}>
          <EditorialCard.Body className="p-0">
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-6 animate-spin text-primary" />
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={users}
                empty="В магазине пока нет сотрудников"
                className="p-3"
              />
            )}
          </EditorialCard.Body>
        </EditorialCard>
      )}

      <UserCreateModal
        open={createOpen}
        scope={{ scope: "shop", shopId: shop.id, shopDisplayName: shop.displayName }}
        onClose={() => setCreateOpen(false)}
        onCreated={(u) => replaceUser(u)}
      />
    </div>
  );
}
